const MULTIOPTION_FIELD_TYPES = new Set([
    'multioption',
    'multioption_with_subforms',
    'lesion_location',
]);

export function defaultEmptyFieldValue(fieldType?: string) {
    return fieldType === 'file' || fieldType === 'multioption' ? [] : '';
}

export function isMultioptionFieldType(fieldType?: string): boolean {
    return !!fieldType && MULTIOPTION_FIELD_TYPES.has(fieldType);
}

export function toggleMultioptionValue(
    currentValue: unknown,
    optionValue: string,
): { multioption: string }[] {
    let next = [{ multioption: optionValue }];
    if (Array.isArray(currentValue) && currentValue.length > 0) {
        const index = currentValue.findIndex(
            (entry) => entry && typeof entry === 'object' && entry.multioption === optionValue,
        );
        if (index !== -1) {
            next = [...currentValue];
            next.splice(index, 1);
        } else {
            next = currentValue.concat(next);
        }
    }
    return next;
}

function tryParseJsonArray(value: string): unknown {
    const trimmed = value.trim();
    if (!trimmed.startsWith('[')) {
        return value;
    }
    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
}

export function isMultioptionValueShape(value: unknown): value is { multioption: string }[] {
    if (!Array.isArray(value) || value.length === 0) {
        return false;
    }
    return value.every(
        (entry) => entry && typeof entry === 'object' && typeof entry.multioption === 'string',
    );
}

/** Resolve field type from schema + stored record; detect multioption by value shape when type is missing. */
export function resolveFieldType(field: { type?: string } | null | undefined, valueRecord: { surveyField?: { type?: string }; value?: unknown } | null | undefined): string | undefined {
    const declaredType = valueRecord?.surveyField?.type || field?.type;
    if (declaredType && MULTIOPTION_FIELD_TYPES.has(declaredType)) {
        return declaredType;
    }

    let value = valueRecord?.value;
    if (typeof value === 'string') {
        value = tryParseJsonArray(value);
    }
    if (isMultioptionValueShape(value)) {
        return 'multioption';
    }
    return declaredType;
}

/** Parse JSON-stringified values from SkincAIr / field-group storage for form + show renderers. */
export function parseStoredFieldValue(value: unknown, fieldType?: string): unknown {
    if (value == null || value === '') {
        return value;
    }

    const resolvedType = fieldType || (isMultioptionValueShape(typeof value === 'string' ? tryParseJsonArray(value) : value) ? 'multioption' : undefined);

    if (resolvedType && MULTIOPTION_FIELD_TYPES.has(resolvedType)) {
        if (typeof value === 'string') {
            const parsed = tryParseJsonArray(value);
            return Array.isArray(parsed) ? parsed : value;
        }
        return value;
    }

    if (resolvedType === 'text_list' && typeof value === 'string' && value.trim().startsWith('[')) {
        const parsed = tryParseJsonArray(value);
        return Array.isArray(parsed) ? parsed : value;
    }

    if ((resolvedType === 'file' || resolvedType === 'image') && typeof value === 'string' && value.trim().startsWith('[')) {
        const parsed = tryParseJsonArray(value);
        return Array.isArray(parsed) ? parsed : value;
    }

    return value;
}

/**
 * The option codes a multi-valued field holds, as plain strings: an `instance-selector`'s
 * referenced instance uuids (SKIN-689), a `multioption`/`lesion_location`'s selected codes.
 * Accepts the array, a `[{value|multioption}]` shape, or the JSON string of either, and a
 * single scalar as a one-element list. One place reads this shape so the editor, the show
 * view and the option-label code cannot drift from the write contract.
 */
export function optionValues(value: unknown): string[] {
    const parsed = typeof value === 'string' ? parseStoredFieldValue(value, 'multioption') : value;
    if (Array.isArray(parsed)) {
        return parsed
            .map((entry: any) => (entry && typeof entry === 'object' ? (entry.value ?? entry.multioption) : entry))
            .filter((v: unknown): v is string => typeof v === 'string' && v !== '');
    }
    return typeof parsed === 'string' && parsed !== '' ? [parsed] : [];
}

/**
 * SKIN-646 — whether an image field's stored value is the "not available" marker the
 * SkincAIr app stores when the operator ticks the checkbox under the camera button
 * (e.g. "No healthy skin available"): `[{"type":"not_available"}]`, as a list or its
 * JSON string. Never mixed with photos.
 */
export function isNotAvailableImageValue(value: unknown): boolean {
    const parsed = typeof value === 'string' ? tryParseJsonArray(value) : value;
    return Array.isArray(parsed) && parsed.length > 0
        && parsed.every(item => item != null && typeof item === 'object' && (item as any).type === 'not_available');
}
